import { baseApi } from '@/store/baseApi';

import type {
  CreateTimeEntryPayload,
  CreateTimeEntryResponse,
  DeleteTimeEntryResponse,
  DuplicateTimeEntryPayload,
  DuplicateTimeEntryResponse,
  GetTimeEntriesParams,
  GroupedTimeEntriesParams,
  GroupedTimeEntriesResponse,
  TimeEntry,
  UpdateTimeEntryPayload,
  UpdateTimeEntryResponse,
} from '@/features/time-entries/types';
import type { APIResponse, PaginatedData } from '@web/types';

export const timeEntriesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGroupedTimeEntries: builder.query<
      APIResponse<GroupedTimeEntriesResponse>,
      GroupedTimeEntriesParams | void
    >({
      query: (params) => ({
        url: 'time-entries/grouped',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'TimeEntries', id: 'GROUPED' }],
    }),
    getTimeEntries: builder.query<
      APIResponse<PaginatedData<TimeEntry>>,
      GetTimeEntriesParams
    >({
      query: (params) => ({
        url: 'time-entries',
        method: 'GET',
        params,
      }),
      providesTags: [{ type: 'TimeEntries', id: 'LIST' }],
    }),
    createTimeEntry: builder.mutation<
      APIResponse<CreateTimeEntryResponse>,
      CreateTimeEntryPayload
    >({
      query: (body) => ({
        url: 'time-entries',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'TimeEntries', id: 'LIST' },
        { type: 'TimeEntries', id: 'GROUPED' },
        { type: 'Tasks', id: 'LIST' },
      ],
    }),
    updateTimeEntry: builder.mutation<
      APIResponse<UpdateTimeEntryResponse>,
      { timeEntryId: string; payload: UpdateTimeEntryPayload }
    >({
      query: ({ timeEntryId, payload }) => ({
        url: `time-entries/${timeEntryId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'TimeEntries', id: 'LIST' },
        { type: 'TimeEntries', id: 'GROUPED' },
        { type: 'Tasks', id: 'LIST' },
      ],
    }),
    deleteTimeEntry: builder.mutation<
      APIResponse<DeleteTimeEntryResponse>,
      { timeEntryId: string }
    >({
      query: ({ timeEntryId }) => ({
        url: `time-entries/${timeEntryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'TimeEntries', id: 'LIST' },
        { type: 'TimeEntries', id: 'GROUPED' },
        { type: 'Tasks', id: 'LIST' },
      ],
    }),
    duplicateTimeEntry: builder.mutation<
      APIResponse<DuplicateTimeEntryResponse>,
      DuplicateTimeEntryPayload
    >({
      query: (body) => ({
        url: 'time-entries/duplicate',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'TimeEntries', id: 'LIST' },
        { type: 'TimeEntries', id: 'GROUPED' },
        { type: 'Tasks', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetGroupedTimeEntriesQuery,
  useGetTimeEntriesQuery,
  useCreateTimeEntryMutation,
  useUpdateTimeEntryMutation,
  useDeleteTimeEntryMutation,
  useDuplicateTimeEntryMutation,
} = timeEntriesApi;
